import { Box, ActionIcon, Group } from '@mantine/core';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useLayoutStore } from '../../stores/layoutStore';
import { colors } from '../../theme/colors';
import { layout } from '../../theme/layout';
import { MaterialIcon } from '../common/MaterialIcon';
import { FieldStatusBadge } from '../common/FieldStatusBadge';
import { DebugCurrentDatePanel } from '../debug/DebugCurrentDatePanel';
import type { FieldState } from '../../types/fieldState';
import type { FieldEvent } from '../../types/fieldEvent';

interface AppHeaderProps {
  onBackToList?: () => void;
  districtName?: string;
  farmName?: string;
  fieldName?: string;
  /** 圃場ステート（バッジ表示用） */
  fieldState?: FieldState;
  /** 圃場イベント一覧（バッジ表示用） */
  fieldEvents?: FieldEvent[];
  /** 情報トグルアイコンを表示するか（モバイルビュー用） */
  showInfoToggle?: boolean;
  /** 情報パネルが表示されているか */
  isInfoPanelVisible?: boolean;
  /** 情報パネルトグルコールバック */
  onToggleInfoPanel?: () => void;
  /** 圃場メモトグルアイコンを表示するか（RightPanel が見えない breakpoint で使用） */
  showMemoToggle?: boolean;
  /** メモオーバーレイが表示されているか（アイコンのアクティブ色制御） */
  isMemoOverlayVisible?: boolean;
  /** メモオーバーレイトグルコールバック */
  onToggleMemoOverlay?: () => void;
}

/**
 * アプリケーションヘッダーコンポーネント
 *
 * レスポンシブ対応:
 * - lg以上: サイドバートグルアイコン（view_sidebar）でサイドバー開閉
 * - md以下: ハンバーガーメニューアイコン（menu）でDrawer開閉
 * - sm未満: パンくずリストを省略形式で表示
 *
 * サイドバー開閉ボタン、戻るボタン、パンくず（地区名 > 生産者名 > 圃場名）を表示
 * 左サイドバー領域を除いた中央・右領域に配置される
 */
export function AppHeader({
  onBackToList,
  districtName,
  farmName,
  fieldName,
  fieldState,
  fieldEvents,
  showInfoToggle,
  isInfoPanelVisible,
  onToggleInfoPanel,
  showMemoToggle,
  isMemoOverlayVisible,
  onToggleMemoOverlay,
}: AppHeaderProps) {
  const { showInlineSidebar, isSm } = useResponsiveLayout();
  const { toggleSidebar, openDrawer } = useLayoutStore();

  // パンくずを構築
  const breadcrumbParts = [districtName, farmName, fieldName].filter(Boolean);

  // sm未満では省略形式（最後の要素のみ表示、または短縮）
  const getBreadcrumb = (): string => {
    if (breadcrumbParts.length === 0) {
      return '\u00A0';
    }

    if (!isSm && breadcrumbParts.length > 1) {
      // sm未満で複数の要素がある場合、最後の要素のみ表示
      return breadcrumbParts[breadcrumbParts.length - 1]!;
    }

    // sm以上または要素が1つの場合、フル表示
    return breadcrumbParts.join(' > ');
  };

  // トグルハンドラ
  const handleToggle = () => {
    if (showInlineSidebar) {
      // lg以上: サイドバートグル
      toggleSidebar();
    } else {
      // md以下: Drawerを開く
      openDrawer();
    }
  };

  // アイコンの選択
  const toggleIcon = showInlineSidebar ? 'view_sidebar' : 'menu';
  const toggleLabel = showInlineSidebar ? 'サイドバーの開閉' : 'メニューを開く';

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: layout.header.padding,
        borderBottom: `1px solid ${colors.gray[200]}`,
        backgroundColor: colors.background.card,
        minHeight: `${layout.header.height}px`,
      }}
    >
      <ActionIcon variant="subtle" color="gray" onClick={handleToggle} aria-label={toggleLabel}>
        <MaterialIcon icon={toggleIcon} size="lg" />
      </ActionIcon>
      <Box
        style={{
          width: '1px',
          height: '20px',
          backgroundColor: colors.gray[300],
          margin: '0 0.75rem',
        }}
      />
      {onBackToList && (
        <ActionIcon variant="subtle" color="gray" onClick={onBackToList} aria-label="一覧に戻る">
          <MaterialIcon icon="arrow_back" size="lg" />
        </ActionIcon>
      )}
      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <h3
          style={{
            margin: 0,
            marginLeft: onBackToList ? layout.spacing.sm : 0,
            fontSize: '1rem',
            fontWeight: 600,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getBreadcrumb()}
        </h3>
        {fieldState && fieldEvents && <FieldStatusBadge state={fieldState} events={fieldEvents} />}
      </Group>
      <DebugCurrentDatePanel />
      {showMemoToggle && onToggleMemoOverlay && (
        <ActionIcon
          variant="subtle"
          color={isMemoOverlayVisible ? 'blue' : 'gray'}
          onClick={onToggleMemoOverlay}
          aria-label={isMemoOverlayVisible ? '圃場メモを閉じる' : '圃場メモを表示'}
        >
          <MaterialIcon icon="chat_bubble_outline" size="lg" />
        </ActionIcon>
      )}
      {showInfoToggle && onToggleInfoPanel && (
        <ActionIcon
          variant="subtle"
          color={isInfoPanelVisible ? 'blue' : 'gray'}
          onClick={onToggleInfoPanel}
          aria-label={isInfoPanelVisible ? '地図を表示' : '圃場情報を表示'}
        >
          <MaterialIcon icon="info" size="lg" />
        </ActionIcon>
      )}
    </Box>
  );
}
